import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Zap, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { PHONES, CONTACT } from '../content';
import { DISTRICT_PAGES } from '../data/districts';
import { SEOHead, BreadcrumbSchema } from '../components/SEOHead';
import Breadcrumb from '../components/Breadcrumb';
import FinalCTA from '../components/FinalCTA';
import ETAIndicator from '../components/ETAIndicator';
import { Link } from 'react-router-dom';

const ETA_DATA = [
  { name: 'İzmit', slug: 'izmit-lastik-yol-yardim', minEta: 10, maxEta: 20, note: 'Merkez — en hızlı müdahale bölgesi' },
  { name: 'Gebze', slug: 'gebze-lastik-yol-yardim', minEta: 15, maxEta: 25, note: 'TEM & D-100 erişimli' },
  { name: 'Darıca', slug: 'darica-lastik-yol-yardim', minEta: 15, maxEta: 25, note: 'Gebze-Darıca arası hızlı erişim' },
  { name: 'Körfez', slug: 'korfez-lastik-yol-yardim', minEta: 15, maxEta: 25, note: 'Yarımca & Hereke dahil' },
  { name: 'Gölcük', slug: 'golcuk-lastik-yol-yardim', minEta: 20, maxEta: 30, note: 'Değirmendere sahil yolu dahil' },
  { name: 'Derince', slug: 'derince-lastik-yol-yardim', minEta: 10, maxEta: 20, note: 'Liman bölgesi — hızlı erişim' },
  { name: 'Çayırova', slug: 'cayirova-lastik-yol-yardim', minEta: 15, maxEta: 25, note: 'Şekerpınar sanayi bölgesi dahil' },
  { name: 'Kartepe', slug: 'kartepe-lastik-yol-yardim', minEta: 20, maxEta: 35, note: 'Maşukiye dağ yolu dahil' },
  { name: 'Başiskele', slug: 'basiskele-lastik-yol-yardim', minEta: 15, maxEta: 25, note: 'Yeniköy & Kullar dahil' },
  { name: 'Karamürsel', slug: 'karamursel-lastik-yol-yardim', minEta: 25, maxEta: 40, note: 'Yalova yolu güzergahı' },
  { name: 'Kandıra', slug: 'kandira-lastik-yol-yardim', minEta: 30, maxEta: 45, note: 'Kuzey Kocaeli' },
  { name: 'Dilovası', slug: 'dilovasi-lastik-yol-yardim', minEta: 15, maxEta: 25, note: 'Sanayi bölgesi — TEM erişimli' },
  { name: 'Hereke', slug: 'hereke-lastik-yol-yardim', minEta: 20, maxEta: 30, note: 'Körfez-Hereke arası' },
];

export default function HizmetSuresi() {
  const tableSchema = {
    "@context": "https://schema.org",
    "@type": "Table",
    "about": "Kocaeli ilçe bazlı lastik yol yardım tahmini varış süreleri",
  };

  return (
    <main data-testid="hizmet-suresi-page" style={{ background: 'var(--bg)' }}>
      <SEOHead
        path="/hizmet-suresi"
        title="Kocaeli Lastik Yol Yardım Varış Süreleri | İlçe Bazlı ETA"
        description="Kocaeli ilçe bazlı lastik yol yardım tahmini varış süreleri. İzmit 10-20 dk, Gebze 15-25 dk, Kartepe 20-35 dk. 7/24 en hızlı müdahale garantisi."
      />
      <BreadcrumbSchema items={[
        { name: 'Anasayfa', url: '/' },
        { name: 'Hizmet Süreleri' },
      ]} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(tableSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section style={{ background: 'var(--bg)', paddingTop: 86, borderBottom: '1px solid var(--border)' }}>
        <div className="wrap" style={{ padding: '3rem 1.5rem 2.5rem' }}>
          <Breadcrumb items={[
            { label: 'Anasayfa', to: '/' },
            { label: 'Hizmet Süreleri' },
          ]} />

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
              fontSize: 'clamp(2rem, 5vw, 3rem)', color: 'var(--text)',
              lineHeight: 1.08, letterSpacing: '-.04em', margin: '.5rem 0',
            }}
          >
            İlçe Bazlı Varış Süreleri
          </motion.h1>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.1rem, 3vw, 1.75rem)', color: 'var(--red)',
            lineHeight: 1.1, letterSpacing: '-.03em', marginBottom: '1rem',
          }}>
            Kocaeli Geneli — 7/24 Aktif
          </p>
          <p style={{ fontSize: 'clamp(.875rem, 1.5vw, 1rem)', color: 'var(--text2)', lineHeight: 1.7, maxWidth: 560 }}>
            Kocaeli'nin tüm ilçelerine mobil lastik yol yardım hizmeti sunuyoruz.
            Aşağıdaki tabloda ilçe bazlı tahmini varış sürelerimizi görebilirsiniz.
            Gerçek süre trafik durumuna göre değişebilir.
          </p>
          <ETAIndicator district="Kocaeli" />
        </div>
      </section>

      {/* TABLE */}
      <section className="section" style={{ background: 'var(--surface)' }}>
        <div className="wrap">
          <div style={{
            borderRadius: 'var(--rl)', overflow: 'hidden',
            border: '1px solid var(--border)', background: 'var(--card)',
          }}>
            {/* Header */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 2fr',
              padding: '.875rem 1.25rem', background: 'var(--surface)',
              borderBottom: '1px solid var(--border)', gap: '1rem',
            }}>
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.8rem', color: 'var(--text)', letterSpacing: '.03em', textTransform: 'uppercase' }}>İlçe</span>
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.8rem', color: 'var(--text)', letterSpacing: '.03em', textTransform: 'uppercase' }}>Tahmini Süre</span>
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.8rem', color: 'var(--text)', letterSpacing: '.03em', textTransform: 'uppercase' }}>Not</span>
            </div>

            {/* Rows */}
            {ETA_DATA.map((row, i) => (
              <Link
                key={row.slug}
                to={`/${row.slug}`}
                data-testid={`eta-row-${row.slug}`}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr 2fr',
                  padding: '.875rem 1.25rem', gap: '1rem',
                  borderBottom: i < ETA_DATA.length - 1 ? '1px solid var(--border)' : 'none',
                  textDecoration: 'none', transition: 'background .15s',
                  alignItems: 'center',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                  <MapPin size={13} style={{ color: 'var(--green)', flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--text)' }}>{row.name}</span>
                </span>
                <span style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                  fontSize: '.9rem', color: row.minEta <= 15 ? 'var(--green)' : row.minEta <= 25 ? '#ff9500' : 'var(--red)',
                }}>
                  {row.minEta}–{row.maxEta} dk
                </span>
                <span style={{ fontSize: '.8rem', color: 'var(--text2)' }}>{row.note}</span>
              </Link>
            ))}
          </div>

          <p style={{ fontSize: '.75rem', color: 'var(--muted)', textAlign: 'center', marginTop: '1rem' }}>
            * Süreler tahminidir ve trafik durumuna göre değişebilir. Gece saatlerinde genellikle daha kısa sürede ulaşıyoruz.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap" style={{ maxWidth: 700 }}>
          <h2 className="h2" style={{ marginBottom: '2rem' }}>
            Nasıl <span style={{ color: 'var(--green)' }}>Çalışır?</span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {[
              { step: '01', title: 'Bizi Arayın', desc: '0534 968 7611 numarasından veya WhatsApp\'tan bize ulaşın. Konumunuzu ve arızanızı bildirin.' },
              { step: '02', title: 'Ekip Yola Çıkar', desc: 'Size en yakın mobil ekibimiz anında yola çıkar. WhatsApp\'tan canlı konum paylaşarak yol takibi yapabilirsiniz.' },
              { step: '03', title: 'Yerinde Müdahale', desc: 'Ekibimiz gelir, arızanızı tespit eder ve yerinde tamir veya değişim yapar. Ortalama 15-20 dakikada işlem tamamlanır.' },
            ].map(({ step, title, desc }) => (
              <div key={step} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900,
                  fontSize: '1.5rem', color: 'var(--muted)', opacity: .3, lineHeight: 1,
                  minWidth: 40,
                }}>
                  {step}
                </div>
                <div>
                  <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1rem', color: 'var(--text)', marginBottom: '.25rem' }}>
                    {title}
                  </h3>
                  <p style={{ fontSize: '.85rem', color: 'var(--text2)', lineHeight: 1.7 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA headline="Hemen Arayın." sub="7/24 Yanınızdayız." />
    </main>
  );
}
